import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { TranslateService } from '@ngx-translate/core';
import { TrainingCategoryFullModel } from 'src/app/models/training-category/training-category.model';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { MyTrainingsFilters } from '../../training/my-trainings/my-trainings.component';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { forkJoin } from 'rxjs';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-training-catalogue-info',
  templateUrl: './training-catalogue-info.component.html',
  styleUrls: ['./training-catalogue-info.component.scss'],
  standalone: false
})
export class TrainingCatalogueInfoComponent {
  trainingCategoryId!: string;
  trainingCategory: TrainingCategoryFullModel | null = null;
  trainingsCourses: any = [];
  eventsList: any = [];
  requiredPersonCourseIds: string[] = [];
  filters: MyTrainingsFilters = {
    trainingTypes: [],
    eventTypes: [],
    searchString: '',
  };

  readonly pageStateKey = 'training-catalogue-details-page';
  
  trainingTypes: any[] = [
    {
      id: TrainingTypeModel.IDD,
      name: this.translateService.instant('COMMON.FIELDS.TRAINING-TYPES.IDD'),
    },
    {
      id: TrainingTypeModel.Compliance,
      name: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
      ),
    },
    {
      id: TrainingTypeModel.General,
      name: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
      ),
    },
  ];
  
  eventTypes: any[] = [
    {
      id: 0,
      name: this.translateService.instant(
        'COMMON.FIELDS.EVENT-TYPES.IN-PERSON'
      ),
    },
    {
      id: 1,
      name: this.translateService.instant('COMMON.FIELDS.EVENT-TYPES.WEBINAR'),
    },
    {
      id: 2,
      name: this.translateService.instant(
        'COMMON.FIELDS.EVENT-TYPES.ELEARNING'
      ),
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public translateService: TranslateService,
    private loaderService: LoadingService,
    private trainingCategoryService: TrainingCategoryService,
    private pageStateService: PageStateService
  ) {
  }

  ngOnInit() {
    this.trainingCategoryId = this.route.snapshot.params['id'];

    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreTrainingCatalogueOverview
    )) {
      this.pageStateService.clear(this.pageStateKey);
    }

    this.loadPageState();
    this.loadTrainingCategoryData();
  }

  loadTrainingCategoryData(): void {
    this.loaderService
      .load(forkJoin([
        this.trainingCategoryService.getTrainingCategorySummary(this.trainingCategoryId),
        this.trainingCategoryService.getRequiredCourseIds(this.trainingCategoryId)
      ]))
      .subscribe(([trainingCategory, requiredPersonCourseIds]) => {
        this.trainingCategory = trainingCategory;
        this.requiredPersonCourseIds = requiredPersonCourseIds;

        this.trainingsCourses = [
          ...trainingCategory.trainings.map((training) => ({
            ...training,
            trainers: training.trainers,
            type: 'Training',
            isLocked: false,
          })),
          ...trainingCategory.courses.map((course) => ({
            ...course,
            trainers: course.trainers,
            imageUrl: course.customImageFile?.fileUrl ?? course.imageFile?.fileUrl,
            type: 'Course',
            isLocked: this.isTrainingLocked(course),
          })),
        ].sort((a, b) => a.name.localeCompare(b.name));

        this.filterEventsList();
      });
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreTrainingCategoriesOverview
    );

    this.router.navigate(['training-catalogue']);
  }

  filterByTrainingType() {
    this.eventsList = this.eventsList.filter((obj: any) => {
      if (
        !this.filters.trainingTypes.length ||
        this.filters.trainingTypes.includes(obj.trainingType)
      ) {
        return true;
      } else {
        return false;
      }
    });
  }

  filterByEventType() {
    if (this.filters.eventTypes.length) {
      let filteredTrainings: any = [];
      if (this.filters.eventTypes.includes(0)) {
        filteredTrainings = [
          ...filteredTrainings,
          ...this.eventsList.filter((obj: any) => {
            return obj.location && obj.location.hasOwnProperty('type') && obj.location.type == 0
              ? true
              : false;
          }),
        ];
      }
      if (this.filters.eventTypes.includes(1)) {
        filteredTrainings = [
          ...filteredTrainings,
          ...this.eventsList.filter((obj: any) => {
            return obj.location && obj.location.type && obj.location.type != 0
              ? true
              : false;
          }),
        ];
      }
      if (this.filters.eventTypes.includes(2)) {
        filteredTrainings = [
          ...filteredTrainings,
          ...(this.eventsList = this.eventsList.filter((obj: any) => {
            return !obj.location && !obj.date ? true : false;
          })),
        ];
      }

      this.eventsList = filteredTrainings;
    }
  }

  filterEventsList() {
    this.eventsList = [... this.trainingsCourses];

    this.filterByTrainingType();
    this.filterByEventType();

    if (!!this.filters.searchString) {
      this.eventsList = this.eventsList.filter((obj: any) => {
        return (
          obj.name
            .toLowerCase()
            .includes(this.filters.searchString.toLowerCase()) ||
          (obj.description || '')
            .toLowerCase()
            .includes(this.filters.searchString.toLowerCase())
        );
      });
    }

    this.eventsList = this.eventsList.sort((a: any, b: any) => {
      const start = a.dates ? new Date(a.dates[0].start).getTime() : null;
      const end = b.dates ? new Date(b.dates[0].start).getTime() : null;

      if (start && end) {
        return start - end;
      } else {
        return 0;
      }
    });

    this.savePageState();
  }

  onSearchInput(event: any) {
    this.filters.searchString = event.value;
    this.filterEventsList();
  }

  onTrainingTypeSelected(e: any) {
    if (e.node.selected) {
      this.filters.trainingTypes = [
        ...new Set([...this.filters.trainingTypes, e.node.key])
      ];
    } else {
      this.filters.trainingTypes = this.filters.trainingTypes.filter(
        (id) => id !== e.node.key
      );
    }
    this.filterEventsList();
  }

  onEventTypeSelected(e: any) {
    if (e.node.selected) {
      if (!this.filters.eventTypes.includes(e.node.key)) {
        this.filters.eventTypes = [
          ...this.filters.eventTypes,
          e.node.key,
        ];
      }
    } else {
      this.filters.eventTypes = this.filters.eventTypes.filter(
        (id) => id !== e.node.key
      );
    }
    this.filterEventsList();
  }

  isTrainingLocked(training: any): boolean {
    return this.requiredPersonCourseIds.includes(training.id);
  }

  private savePageState(): void {
    this.pageStateService.save(this.pageStateKey, {
      filters: this.filters,
    });
  }
  
  private loadPageState(): void {
    const state = this.pageStateService.load<{
      filters: {
        trainingTypes: number[];
        trainingCategories?: string[];
        eventTypes: number[];
        searchString: string;
      }
    }>(this.pageStateKey);

    if (!state) return;

    this.filters = {
      ...this.filters,
      ...state.filters
    };
  }

  syncTreeSelection(e: any, selectedIds?: any[] | null): void {
    if (!selectedIds?.length) {
      return;
    }

    e.component.unselectAll();

    selectedIds.forEach(id => e.component.selectItem(id));
  }
}
